import { useSEO } from "@/hooks/useSEO";
import { SeoPageLayout } from "@/components/seo/SeoPageLayout";
import { SeoSection, SeoSubSection, SeoList } from "@/components/seo/SeoContent";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";

const SITE = "https://www.regard-de-nature.fr";

const Iridologie = () => {
  useSEO({
    title: "L'iridologie : observer l'iris pour comprendre son terrain – Regard de Nature",
    description:
      "L'iridologie, technique d'observation de l'iris, complète le bilan naturopathique. Découvrez son utilité, son déroulement et son lien avec la naturopathie.",
    canonical: `${SITE}/iridologie`,
  });

  return (
    <SeoPageLayout
      h1="L'iridologie : comprendre votre terrain à travers l'observation de l'iris"
      intro="Vos yeux racontent quelque chose de votre constitution, de vos forces et de vos sensibilités. L'iridologie est une technique d'observation douce que j'utilise pour enrichir le bilan naturopathique — sans jamais poser de diagnostic."
    >
      <SeoSection title="Qu'est-ce que l'iridologie ?">
        <p>
          L'iridologie est une technique d'observation de l'iris — la partie colorée de l'œil. Elle s'appuie sur
          l'idée que la trame, la couleur, les reliefs et les marques de l'iris peuvent refléter certaines
          caractéristiques constitutionnelles d'une personne, ainsi que ses sensibilités et points de force
          naturels.
        </p>
        <p className="font-semibold text-primary">
          L'iridologie n'est pas une médecine. Elle ne permet pas de poser un diagnostic, ni de détecter ou de
          traiter une maladie. C'est un outil d'observation utilisé en complément d'un bilan naturopathique.
        </p>
      </SeoSection>

      <SeoSection title="Concrètement, à quoi ça sert ?">
        <p>
          Lors d'un bilan, l'observation de l'iris m'aide à mieux comprendre votre terrain : votre constitution
          générale, vos zones de sensibilité, vos forces. Cela enrichit la lecture globale de votre situation et
          permet de personnaliser davantage les conseils d'hygiène de vie. J'observe par exemple si votre terrain
          a tendance à être plutôt nerveux, digestif, lymphatique, et j'adapte mes propositions en conséquence.
        </p>
        <SeoList
          items={[
            "Mieux cerner votre constitution et votre terrain de base",
            "Repérer des zones potentiellement plus sensibles à soutenir",
            "Personnaliser le programme d'hygiène de vie",
            "Compléter les autres éléments du bilan (anamnèse, mode de vie, ressentis)",
          ]}
        />
      </SeoSection>

      <SeoSection title="Dans quels cas c'est particulièrement utile ?">
        <SeoList
          items={[
            "Vous avez l'impression de « tirer sur la corde » sans comprendre pourquoi.",
            "Vous voulez mieux connaître votre terrain pour agir en prévention.",
            "Vous cherchez un éclairage complémentaire avant de vous engager dans un suivi.",
            "Vous traversez une période de transition (cycle, périnatalité, ménopause, reconversion).",
            "Vous êtes simplement curieux·se de mieux vous comprendre.",
          ]}
        />
      </SeoSection>

      <SeoSection title="Comment se déroule une séance d’iridologie ?">
        <SeoSubSection title="L’observation">
          <p>
            Concrètement, je réalise des photographies haute définition de vos yeux à l’aide d’un outil adapté.
            L’examen est indolore, rapide et non invasif. Vous restez assis·e confortablement pendant la prise de
            vue. Les images sont ensuite analysées après la séance.
          </p>
        </SeoSubSection>
        <SeoSubSection title="L’analyse">
          <p>
            J’étudie la trame de l’iris, sa couleur et ses différentes zones en m’appuyant sur les cartographies
            iridologiques de référence. Cette analyse s’intègre dans une lecture globale de votre situation, en
            lien avec votre histoire et vos ressentis.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Les recommandations">
          <p>
            Les éléments observés viennent compléter les conseils que je vous propose : alimentation, hygiène de
            vie, soutiens naturels. L’iridologie est un outil complémentaire dans l’accompagnement. Un compte
            rendu est ensuite transmis dans votre programme d’hygiène de vie.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Iridologie et naturopathie : une complémentarité">
        <p>
          Dans ma pratique, l’iridologie est rarement utilisée seule. Elle s’intègre au bilan naturopathique
          global et apporte un éclairage supplémentaire sur votre terrain. La{" "}
          <Link to="/mon-approche" className="text-primary underline hover:text-secondary">naturopathie</Link>{" "}
          reste le cadre principal de l’accompagnement.
        </p>
        <p>
          Cette complémentarité permet d’affiner l’accompagnement de manière personnalisée, dans une démarche
          basée sur l’observation, l’écoute et la pédagogie, sans visée diagnostique ni thérapeutique.
        </p>
      </SeoSection>

      <SeoSection title="Questions fréquentes">
        <SeoSubSection title="Est-ce que ça remplace un examen chez l'ophtalmologue ?">
          <p>
            Non, absolument pas. L'iridologie est une technique d'observation naturopathique, sans visée médicale.
            En cas de symptôme oculaire (douleur, baisse de vision, irritation), consultez un ophtalmologue.
          </p>
        </SeoSubSection>
        <SeoSubSection title="Combien de temps dure une séance ?">
          <p>
            La prise de photos prend quelques minutes. L'analyse et l'échange autour des observations s'inscrivent
            dans le temps du bilan naturopathique (1 h 30 environ).
          </p>
        </SeoSubSection>
        <SeoSubSection title="Faut-il faire ça à chaque séance ?">
          <p>
            Non. Les caractéristiques observées en iridologie évoluent très lentement. Une observation lors du
            bilan initial suffit généralement, parfois revue à l'occasion d'un suivi long.
          </p>
        </SeoSubSection>
      </SeoSection>

      <SeoSection title="Pour qui ?">
        <SeoList
          items={[
            "Adultes souhaitant approfondir la compréhension de leur terrain",
            "Personnes en recherche d'une approche d'observation douce",
            "Familles désireuses d'un éclairage complémentaire",
            "Toute personne curieuse de mieux se connaître",
          ]}
        />
        <p className="italic text-foreground/70 mt-4">
          L'iridologie ne se substitue pas à un examen ophtalmologique ni à un suivi médical. En cas de symptôme
          oculaire, consultez un professionnel de santé.
        </p>
      </SeoSection>
    </SeoPageLayout>
  );
};

export default Iridologie;
